import React, { useEffect, useMemo, useState } from 'react'
import { useDebouncedCallback } from 'use-debounce'
import T from 'prop-types'
import { useTranslation } from 'react-i18next'
import theme from 'theme'
import { useNavigate } from 'react-router-dom'
import {
  Box,
  CircularProgress,
  Collapse,
  IconButton,
  Link as MuiLink,
  Tooltip,
} from '@mui/material'
import { mapLocation } from 'lib/carDetailsLib'
import { mapMessagesForDownload } from 'lib/messagesLib'
import { isWithinInterval } from 'date-fns'
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined'
import HeartDownloadIcon from 'assets/images/icons/heart-download'

import PageTitle from 'components/atoms/PageTitle'
import SearchField from 'components/atoms/SearchField'
import ConfirmBar from 'components/atoms/ConfirmBar'
import CarFiltersColumn from 'components/atoms/CarsFiltersColumn'
import CarCard from 'components/atoms/CarCard'
import CarsList from 'components/atoms/CarsList'
import PageSessionSelection from 'components/atoms/PageSessionSelection'
import DownloadBar from 'components/atoms/DownloadBar'
import PagePartnersSelect from 'components/atoms/PagePartnersSelect'
import InaccessibleSession from 'components/atoms/InaccessibleSession'

import useCarsFilters from 'hooks/useCarsFilters'
import useInputOutput from 'hooks/useInputOutput'
import useExport from 'hooks/useExport'

import CarsBookedCarDetails from '../CarsBookedCarDetails'

import {
  getCarsBookedDisplayStatus,
  getCarsBookedDisplayedReprice,
  getCarsBookedUrl,
} from './carsBookedLib'
import CarBookedCarAdditionalDetails from './CarBookedCarAdditionalDetails'
import CarsBookedChoicesRecap from './CarsBookedChoicesRecap'

function getRowsForDownload(titlesRow, cars, t, session) {
  return [
    titlesRow,
    ...cars.map((car) => {
      return [
        `${car.title}`,
        car.price || '-',
        ...(session.canReprice
          ? [getCarsBookedDisplayedReprice(car) || '-']
          : []),
        car.km,
        mapLocation(car.location),
        t(`carDetails.mapping.${car.transmission}`),
        car.reconditioningEstCost || '-',
        car.year,
        car.fuelType,
        car.plate,
        Math.round(car.optionAmount) || '-',
        t(`carsBooked.status.${getCarsBookedDisplayStatus(car)}`),
        car.messages ? mapMessagesForDownload(car.messages, t) : '-',
        car.favorite
          ? t('common.filters.boolean.true')
          : t('common.filters.boolean.false'),
      ]
    }),
  ]
}

const CarsBookedInaccessibleSession = ({
  hasAccessToSession,
  isInPublication,
}) => {
  const { t } = useTranslation()
  return (
    <InaccessibleSession hideAction>
      {!hasAccessToSession
        ? t('carsBooked.carsToBuyNoAccess')
        : isInPublication
        ? t('carsBooked.carsToBuyStillOpen')
        : t('carsBooked.carsToBuyNoChoices')}
    </InaccessibleSession>
  )
}

// eslint-disable-next-line sonarjs/cognitive-complexity
export default function CarsBooked({
  session,
  partnerId,
  partners,
  cars,
  sessions,
  currentActiveSession,
  hasAccessToSession,
  readOnly,
  showFavorites = false,
  showPriceRange = true,
}) {
  const { t } = useTranslation()

  const navigate = useNavigate()

  const [showDetails, setShowDetails] = useState()
  const [filteredCars, setFilteredCars] = useState([])

  const { saveExport, isFetching: isFetchingExport } = useExport()

  const {
    filters,
    filtersData,
    initFilters,
    setKmRange,
    setDamageRange,
    setYearRange,
    setBrands,
    setModels,
    setLocations,
    setFuels,
    setCategory,
    setQuery,
    setCarsBookedType,
    setOnlyFavorites,
    filterCars,
    resetFilters,
  } = useCarsFilters()

  useEffect(() => {
    if (!cars.length) {
      return
    }

    initFilters(cars)
  }, [cars, initFilters])

  const handleSetFilteredCars = useDebouncedCallback(
    (cars, filters, setFilteredCars, filterCars) =>
      setFilteredCars(filterCars(cars, filters)),
    100
  )

  useEffect(() => {
    handleSetFilteredCars(cars, filters, setFilteredCars, filterCars)
  }, [cars, handleSetFilteredCars, filters, setFilteredCars, filterCars])

  const handleChangeSearch = (event) => {
    setQuery(event.target.value)
  }

  const carsContent = useMemo(
    () =>
      filteredCars.map((car, index) => (
        <CarCard
          data-testid={`car-${index}`}
          key={car.originalId}
          height={theme.custom.carCardAdditionalMaxHeight}
          car={car}
          price={car.price}
          maxPrice={car.maxPrice}
          showPriceRange={showPriceRange}
          onClick={() => setShowDetails(car.plate)}
          showFavoriteReadOnly={showFavorites}
          right={
            <>
              <Box />
              <MuiLink
                color="primary"
                data-testid="car-detail-right-click"
                sx={{ cursor: 'pointer' }}
                onClick={() => setShowDetails(car.plate)}
                component={Box}
                variant="body2"
              >
                <strong>{t('carsBooked.details')}</strong>
              </MuiLink>
            </>
          }
          bottom={
            <CarBookedCarAdditionalDetails
              car={car}
              canReprice={session?.canReprice}
            />
          }
        />
      )),
    [filteredCars, session, setShowDetails, t, showFavorites, showPriceRange]
  )

  const carFilters = useMemo(
    () => (
      <CarFiltersColumn
        filters={filters}
        filtersData={filtersData}
        filteredCars={
          filteredCars?.length !== cars.length ? filteredCars : undefined
        }
        setKmRange={setKmRange}
        setDamageRange={setDamageRange}
        setYearRange={setYearRange}
        setBrands={setBrands}
        setModels={setModels}
        setLocations={setLocations}
        setFuels={setFuels}
        setCategory={setCategory}
        resetFilters={resetFilters}
        readOnly={readOnly}
      />
    ),
    [
      filters,
      filtersData,
      filteredCars,
      cars.length,
      setKmRange,
      setDamageRange,
      setYearRange,
      setBrands,
      setModels,
      setLocations,
      setFuels,
      setCategory,
      resetFilters,
      readOnly,
    ]
  )

  const handleSetFilteringRecapValues = (value) => {
    setCarsBookedType(value)
    if (filters.onlyFavorites) {
      setOnlyFavorites(false)
    }
  }

  const handleToggleFavoritesFilter = () => {
    const newValue = !filters.onlyFavorites
    setOnlyFavorites(newValue)
    // If turning on favorites filter, reset the carsBookedType filter
    if (newValue && filters.carsBookedType) {
      setCarsBookedType('')
    }
  }

  const isInPublication = useMemo(
    () =>
      isWithinInterval(new Date(), {
        start: new Date(session?.agenda?.publication?.startsAt),
        end: new Date(session?.agenda?.publication?.endsAt),
      }),
    [
      session?.agenda?.publication?.endsAt,
      session?.agenda?.publication?.startsAt,
    ]
  )

  const { saveXls } = useInputOutput()

  const handleDownload = async () => {
    const titlesRow = [
      t('common.excel.name'),
      t('common.excel.price'),
      ...(session.canReprice ? [t('common.excel.reprice')] : []),
      t('carDetails.km'),
      t('carDetails.location'),
      t('carDetails.transmission'),
      t('carDetails.damage'),
      t('carDetails.year'),
      t('carDetails.fuelType'),
      t('carDetails.plate'),
      t('carDetails.optionals'),
      t('common.excel.status'),
      t('common.excel.messages'),
      t('common.excel.favorite'),
    ]

    const rows = getRowsForDownload(titlesRow, cars, t, session)

    saveXls(`cars-booked`, rows)
  }

  const handleDownloadFavorites = async () => {
    const favoriteCars = cars.filter((car) => car.favorite)

    if (favoriteCars.length === 0) {
      return
    }

    const titlesRow = [
      t('common.excel.name'),
      t('common.excel.price'),
      ...(session.canReprice ? [t('common.excel.reprice')] : []),
      t('carDetails.km'),
      t('carDetails.location'),
      t('carDetails.transmission'),
      t('carDetails.damage'),
      t('carDetails.year'),
      t('carDetails.fuelType'),
      t('carDetails.plate'),
      t('carDetails.optionals'),
      t('common.excel.status'),
      t('common.excel.messages'),
      t('common.excel.favorite'),
    ]

    const rows = getRowsForDownload(titlesRow, favoriteCars, t, session)

    saveXls(`cars-booked-favorites`, rows)
  }

  const handleAdditionalFileDownload = async () => {
    await saveExport({
      name: `dati_veicoli_${session.sessionId}`,
      params: { partnerId, sessionId: session.sessionId },
      path: 'carsBooked',
    })
  }

  const handlePartnerChange = (event) => {
    navigate(getCarsBookedUrl(session?.sessionId, event.target.value))
  }

  return showDetails ? (
    <CarsBookedCarDetails
      plate={showDetails}
      partnerId={partnerId}
      session={session}
      cars={filteredCars}
      setShowDetails={setShowDetails}
      showFavorites={showFavorites}
      showPriceRange={showPriceRange}
    />
  ) : (
    <>
      <PageTitle
        label={t('carsBooked.title')}
        rightColumn={
          <>
            <PageSessionSelection
              session={session}
              currentActiveSession={currentActiveSession}
              handleStepUrl={(sessionId) =>
                getCarsBookedUrl(sessionId, partnerId)
              }
              sessionsList={sessions}
              statusLabel={t('carsBooked.assignmentStatus')}
              task="assignment"
              readOnly={readOnly}
            />
            {partners.length > 0 && (
              <>
                <Box pr={2} />
                <PagePartnersSelect
                  partners={partners}
                  value={partnerId}
                  handleChange={handlePartnerChange}
                  readOnly={readOnly}
                />
              </>
            )}
          </>
        }
      />
      {!hasAccessToSession || !cars?.length || isInPublication ? (
        <CarsBookedInaccessibleSession
          hasAccessToSession={hasAccessToSession}
          isInPublication={isInPublication}
        />
      ) : (
        <>
          <ConfirmBar
            left={
              <SearchField
                placeholder={t('common.form.searchBy')}
                value={filters.query}
                onChange={handleChangeSearch}
                data-testid="text-search"
              />
            }
            leftProps={{ sx: { width: 0.5 } }}
            top={
              <CarsBookedChoicesRecap
                cars={cars}
                filters={filters}
                setFilteringRecapValues={handleSetFilteringRecapValues}
                onlyFavorites={
                  showFavorites ? filters.onlyFavorites : undefined
                }
                onToggleFavorites={
                  showFavorites ? handleToggleFavoritesFilter : undefined
                }
              />
            }
            topProps={{
              sx: {
                borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
              },
            }}
            right={
              <>
                <Collapse orientation="horizontal" in={false}>
                  {/* <Collapse orientation="horizontal" in={isFetchingExport}> */}
                  <div style={{ marginLeft: 4, marginRight: 4 }}>
                    <CircularProgress size={20} />
                  </div>
                </Collapse>
                <DownloadBar handleDownload={handleDownload} />
                {showFavorites && (
                  <Tooltip title={t('carsBooked.downloadFavorites')}>
                    <IconButton
                      data-testid="download-favorites"
                      aria-label={t('carsBooked.downloadFavorites')}
                      onClick={handleDownloadFavorites}
                      disabled={!cars.some((car) => car.favorite)}
                      sx={{ ml: 0.5 }}
                    >
                      <HeartDownloadIcon
                        style={{ fontSize: 20, color: 'black' }}
                      />
                    </IconButton>
                  </Tooltip>
                )}
                <IconButton
                  data-testid="download-additional-file"
                  aria-label={t('carsToBuy.downloadAdditionalFile')}
                  onClick={handleAdditionalFileDownload}
                  disabled={isFetchingExport}
                >
                  <FolderOutlinedIcon style={{ color: 'black' }} />
                </IconButton>
              </>
            }
            hideConfirm
            sticky
          />
          <CarsList left={carFilters} right={carsContent} />
        </>
      )}
    </>
  )
}

CarsBooked.propTypes = {
  session: T.object.isRequired,
  partnerId: T.string,
  partners: T.array,
  cars: T.array,
  sessions: T.array,
  currentActiveSession: T.object,
  hasAccessToSession: T.bool,
  readOnly: T.bool,
  showFavorites: T.bool,
  showPriceRange: T.bool,
}
